import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

type DrillType = 'received' | 'pending' | 'overdue';

interface PaymentsDrillDownModalProps {
  open: boolean;
  onClose: () => void;
  type: DrillType;
  period: 'today' | 'week' | 'month';
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// IST-safe YYYY-MM-DD formatter — avoids the UTC drift of .toISOString().slice(0,10)
const istYmd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getPeriodRange = (period: 'today' | 'week' | 'month'): { from: string; to: string; label: string } => {
  const now = new Date();
  if (period === 'today') {
    const ymd = istYmd(now);
    return { from: ymd, to: ymd, label: 'Today' };
  }
  if (period === 'week') {
    const day = now.getDay() || 7; // Sunday = 7
    const monday = new Date(now);
    monday.setDate(now.getDate() - day + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: istYmd(monday), to: istYmd(sunday), label: 'This Week' };
  }
  // month
  const y = now.getFullYear();
  const m = now.getMonth();
  const lastDay = new Date(y, m + 1, 0).getDate();
  return {
    from: `${y}-${String(m + 1).padStart(2, '0')}-01`,
    to: `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    label: now.toLocaleString('en-IN', { month: 'long', year: 'numeric' }),
  };
};

const TITLES: Record<DrillType, string> = {
  received: 'Cash In',
  pending: 'Pending Payments',
  overdue: 'Overdue Payments',
};

export const PaymentsDrillDownModal: React.FC<PaymentsDrillDownModalProps> = ({ open, onClose, type, period }) => {
  const range = getPeriodRange(period);

  const { data, isLoading, error } = useQuery({
    queryKey: ['payments-drilldown', type, range.from, range.to],
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('dateFrom', range.from);
      qs.set('dateTo', range.to);
      qs.set('limit', '200');
      // 'received' → filter to actual cash in; others show raw (backend computes pending/overdue separately)
      const res = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/payments?${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch payments (${res.status})`);
      const body = await res.json();
      return body.data || body.payments || [];
    },
    enabled: open,
  });

  if (!open) return null;

  const payments: any[] = Array.isArray(data) ? data : [];
  // For 'received' drill-down, filter to RECEIVED/VERIFIED and non-refund
  const filtered =
    type === 'received'
      ? payments.filter((p) => ['RECEIVED', 'VERIFIED'].includes(p.status) && !p.isRefund)
      : payments;

  const total = filtered.reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {TITLES[type]} · {range.label}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isLoading
                ? 'Loading...'
                : `${filtered.length} ${filtered.length === 1 ? 'payment' : 'payments'} totaling ${formatCurrency(total)}`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading payments...
            </div>
          ) : error ? (
            <div className="py-16 text-center text-red-600 text-sm">
              {(error as Error).message}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-500 text-sm">
              No payments found for this period.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th scope="col" className="text-left px-4 py-2 font-medium text-gray-600">Date</th>
                  <th scope="col" className="text-left px-4 py-2 font-medium text-gray-600">Client</th>
                  <th scope="col" className="text-right px-4 py-2 font-medium text-gray-600">Amount</th>
                  <th scope="col" className="text-left px-4 py-2 font-medium text-gray-600">Method</th>
                  <th scope="col" className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th scope="col" className="text-left px-4 py-2 font-medium text-gray-600"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-700">{formatDate(p.paymentDate)}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900">{p.lead?.name || '—'}</div>
                      {p.lead?.company && <div className="text-xs text-gray-500">{p.lead.company}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-green-700">{formatCurrency(p.amount)}</td>
                    <td className="px-4 py-2.5 text-gray-700 text-xs uppercase tracking-wide">
                      {(p.paymentMethod || '—').replace('_', ' ')}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          p.status === 'VERIFIED'
                            ? 'bg-green-100 text-green-700'
                            : p.status === 'RECEIVED'
                            ? 'bg-blue-100 text-blue-700'
                            : p.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {p.leadId && (
                        <a
                          href={`/leads/${p.leadId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-600"
                          title="Open lead in new tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-700" colSpan={2}>
                    Total
                  </td>
                  <td className="px-4 py-2 text-right font-bold text-gray-900">{formatCurrency(total)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentsDrillDownModal;
